import {
  searchContractManagementViews,
  createContractManagementView,
  createContractManagementViewFromPreview,
  getContractManagementView,
  updateContractManagementView,
  deleteContractManagementView,
  addColumn,
  addContractTypeColumns,
  updateCmViewQuestionColumn,
  updateCmViewQuestionColumnQuestion,
  deleteCmViewQuestionColumn,
  getCmViewQuestionColumn,
  getContractManagementViewPreview,
  getContractManagementViewTable,
  getContractManagementViewTableFilterOptions,
  getContractManagementViewTableContractFiles,
  addViewSourceDirectories,
  removeViewSourceDirectories,
  listViewSourceDirectories,
  addViewSourceContracts,
  removeViewSourceContracts,
  listViewSourceContracts,
  type ContractManagementCalendarLinkResult,
  type ContractManagementContractInDbSlim,
  type VaultDirectoryInDbSlim,
  ContractManagementColumnExtractionScope,
  type ContractManagementViewInDb,
  type ContractManagementViewCreate,
  type ContractManagementViewCreateFromPreview,
  type ContractManagementViewUpdate,
  type ContractManagementViewSearchResult,
  type FilterableSearchQuery,
  type LimitableSearchableQuery,
  type ContractManagementViewColumnInDb,
  type ContractManagementViewColumnBulkAddResult,
  type ContractManagementViewColumnCreate,
  type ContractManagementViewColumnQuestionCreate,
  type ContractManagementViewColumnUpdate,
  type ContractManagementViewPreviewRequest,
  type ContractManagementViewPreviewTableResponse,
  type ContractManagementViewTable,
  type ContractManagementViewTableContractFiles,
  type ContractManagementViewTableFilterOptions,
} from '@/api';

/**
 * Extract error message from various error types
 */
function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === 'object' && err !== null && 'detail' in err) {
    const detail = err.detail;
    return String(detail);
  }
  return defaultMessage;
}

export const ContractViewsService = {
  /**
   * Search contract management views with pagination and filtering
   */
  async search(
    query: {
      text?: string;
      page?: number;
      pageSize?: number;
      sortKey?: string;
      sortDirection?: 'asc' | 'desc';
      columnFilters?: Record<string, string[]>;
    } = {}
  ): Promise<ContractManagementViewSearchResult> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query.pageSize || 10,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        offset: query.page ? (query.page - 1) * (query.pageSize || 10) : 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query.sortKey || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query.sortDirection || 'desc',
        similarity_threshold: 0.5,
        column_filters: query.columnFilters ?? {},
      };

      const response = await searchContractManagementViews({ body: searchQuery });

      if (!response.data) {
        throw new Error('Failed to search contract management views');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to search contract management views. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Fetch a single contract management view by UID
   */
  async getById(uid: string): Promise<ContractManagementViewInDb> {
    try {
      const response = await getContractManagementView({ path: { view_uid: uid } });

      if (!response.data) {
        throw new Error('Failed to fetch contract management view');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to fetch contract management view. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Create a new contract management view
   */
  async create(viewData: ContractManagementViewCreate): Promise<ContractManagementViewInDb> {
    try {
      const response = await createContractManagementView({
        body: viewData,
      });

      if (!response.data) {
        throw new Error('Failed to create contract management view');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to create contract management view. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Create a view from preview configuration
   *
   * This creates a complete view in a single API call, including:
   * - View metadata (name, description)
   * - The scope: picked folders (with their subtrees) and contracts
   * - Custom columns with their questions and filters
   * - Static column configuration (visibility, order, filters)
   *
   * This is the preferred method for creating views from the wizard
   * as it handles everything atomically on the backend.
   *
   * @param previewData The complete preview configuration
   * @returns The created view
   */
  async createFromPreview(
    previewData: ContractManagementViewCreateFromPreview
  ): Promise<ContractManagementViewInDb> {
    try {
      const response = await createContractManagementViewFromPreview({
        body: previewData,
      });

      if (!response.data) {
        throw new Error('Failed to create view from preview');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to create view from preview. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Add a column to an existing view
   */
  async addColumn(
    viewUid: string,
    columnData: ContractManagementViewColumnCreate,
    questionData: ContractManagementViewColumnQuestionCreate
  ): Promise<ContractManagementViewColumnInDb> {
    try {
      const response = await addColumn({
        path: { view_uid: viewUid },
        body: {
          column_create_schema: columnData,
          question_create_schema: questionData,
        },
      });

      if (!response.data) {
        throw new Error('Failed to add column to view');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to add column to view. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Add a column per extraction prompt of a contract type and dispatch their extraction.
   * Prompts whose question a column of the view already asks are skipped; those columns come
   * back as `existing`.
   */
  async addContractTypeColumns(
    viewUid: string,
    contractTypeUid: string
  ): Promise<ContractManagementViewColumnBulkAddResult> {
    try {
      const response = await addContractTypeColumns({
        path: { view_uid: viewUid },
        query: { contract_type_uid: contractTypeUid },
      });

      if (!response.data) {
        throw new Error("Failed to add the contract type's prompts as columns");
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          "Failed to add the contract type's prompts as columns. Please try again."
        ),
        { cause: error }
      );
    }
  },

  /**
   * Update an existing contract management view
   */
  async update(
    uid: string,
    updateData: ContractManagementViewUpdate
  ): Promise<ContractManagementViewInDb> {
    try {
      const response = await updateContractManagementView({
        path: { view_uid: uid },
        body: updateData,
      });

      if (!response.data) {
        throw new Error('Failed to update contract management view');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update contract management view. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Update column visibility
   */
  async updateColumnVisibility(
    columnUid: string,
    visible: boolean
  ): Promise<ContractManagementViewColumnInDb> {
    try {
      const updateData: ContractManagementViewColumnUpdate = {
        visible,
      };

      const response = await updateCmViewQuestionColumn({
        path: { view_column_uid: columnUid },
        body: updateData,
      });

      if (!response.data) {
        throw new Error('Failed to update column visibility');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update column visibility. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Delete a contract management view
   * @returns The deleted view
   */
  async delete(uid: string): Promise<ContractManagementViewInDb> {
    try {
      const response = await deleteContractManagementView({ path: { view_uid: uid } });

      if (!response.data) {
        throw new Error('Failed to delete contract management view - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to delete contract management view. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get a single column by UID
   */
  async getColumnById(columnUid: string): Promise<ContractManagementViewColumnInDb> {
    try {
      const response = await getCmViewQuestionColumn({
        path: { view_column_uid: columnUid },
      });

      if (!response.data) {
        throw new Error('Failed to fetch column');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to fetch column. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Update an existing column
   */
  async updateColumn(
    columnUid: string,
    columnData: ContractManagementViewColumnUpdate
  ): Promise<ContractManagementViewColumnInDb> {
    try {
      const response = await updateCmViewQuestionColumn({
        path: { view_column_uid: columnUid },
        body: columnData,
      });

      if (!response.data) {
        throw new Error('Failed to update column');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to update column. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Update a column's question
   * @param extractionScope How many of the view's existing contracts to re-extract with the new
   * question (the backend requires this explicitly - there is no default)
   */
  async updateColumnQuestion(
    columnUid: string,
    questionData: ContractManagementViewColumnQuestionCreate,
    extractionScope: ContractManagementColumnExtractionScope
  ): Promise<ContractManagementViewColumnInDb> {
    try {
      const response = await updateCmViewQuestionColumnQuestion({
        path: { view_column_uid: columnUid },
        query: { extraction_scope: extractionScope },
        body: questionData,
      });

      if (!response.data) {
        throw new Error('Failed to update column question');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update column question. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Delete a column
   * @returns The deleted column
   */
  async deleteColumn(columnUid: string): Promise<ContractManagementViewColumnInDb> {
    try {
      const response = await deleteCmViewQuestionColumn({
        path: { view_column_uid: columnUid },
      });

      if (!response.data) {
        throw new Error('Failed to delete column - no data returned from API');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to delete column. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Get preview table data for view creation wizard
   *
   * This endpoint is used during the step-by-step view creation process
   * to preview how the view table will look with the picked contracts and columns.
   * No database records are created - this is purely for preview purposes.
   *
   * The preview supports:
   * - Step 2: Scope - contracts in the picked folders, plus picked contracts, are shown
   * - Step 3: Column configuration - columns are shown with their settings
   *
   * For dynamic columns (custom extraction columns), values will be None
   * since no extractions exist yet. For static columns (contractual parties,
   * matters, …), actual data from the contracts will be shown.
   *
   * @param request The preview request containing the scope, columns, and static column settings
   * @returns Preview table response with contracts and column info
   */
  async getPreviewTable(
    request: ContractManagementViewPreviewRequest
  ): Promise<ContractManagementViewPreviewTableResponse> {
    try {
      const response = await getContractManagementViewPreview({
        body: request,
      });

      if (!response.data) {
        throw new Error('Failed to get view preview table');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to get view preview table. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get table data for a contract management view with extraction values
   * @param viewUid The UID of the view
   * @param query Optional search query parameters
   * @returns Table data with contracts and their extraction values
   */
  async getViewTable(
    viewUid: string,
    query?: Partial<FilterableSearchQuery>
  ): Promise<ContractManagementViewTable> {
    try {
      const searchQuery: FilterableSearchQuery = {
        text: query?.text ?? '',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query?.limit || 50,
        offset: query?.offset ?? 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query?.sort_key || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query?.sort_direction || 'desc',
        similarity_threshold: query?.similarity_threshold ?? 0.5,
        ...(query?.column_filters && { column_filters: query.column_filters }),
      };

      const response = await getContractManagementViewTable({
        path: { view_uid: viewUid },
        body: searchQuery,
      });

      if (!response.data) {
        throw new Error('Failed to get view table');
      }

      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to get view table. Please try again.'), {
        cause: error,
      });
    }
  },

  /**
   * Get the per-column filter options of a view's table. They depend only on the
   * view's scope, not on the table query, so they are fetched apart from the rows.
   * @param viewUid The UID of the view
   * @returns Filter options keyed by column
   */
  async getViewTableFilterOptions(
    viewUid: string
  ): Promise<ContractManagementViewTableFilterOptions> {
    try {
      const response = await getContractManagementViewTableFilterOptions({
        path: { view_uid: viewUid },
      });

      if (!response.data) {
        throw new Error('Failed to get view filter options');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to get view filter options. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Get contract files for a contract in a view table with extraction values
   * @param viewUid The UID of the view
   * @param contractUid The UID of the contract
   * @param query Optional search query parameters (pagination and sorting only)
   * @returns Contract files with extraction values, ordered by visible columns sorted by order_index
   */
  async getContractFilesForViewTable(
    viewUid: string,
    contractUid: string,
    query?: Partial<LimitableSearchableQuery>
  ): Promise<ContractManagementViewTableContractFiles> {
    try {
      const searchQuery: LimitableSearchableQuery = {
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- 0 must use the fallback, not be preserved
        limit: query?.limit || 50,
        offset: query?.offset ?? 0,
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_key: query?.sort_key || 'created_at',
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
        sort_direction: query?.sort_direction || 'desc',
      };

      const response = await getContractManagementViewTableContractFiles({
        path: {
          view_uid: viewUid,
          contract_uid: contractUid,
        },
        body: searchQuery,
      });

      if (!response.data) {
        throw new Error('Failed to get contract files for view table');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to get contract files for view table. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  // ==================== Scope: source folders and contracts ====================
  // A folder brings its whole subtree; a view with neither covers every
  // contract in the main vault.

  async listSourceDirectories(viewUid: string): Promise<VaultDirectoryInDbSlim[]> {
    try {
      const response = await listViewSourceDirectories({ path: { view_uid: viewUid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load the view’s folders.'), {
        cause: error,
      });
    }
  },

  async addSourceDirectories(
    viewUid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addViewSourceDirectories({
        path: { view_uid: viewUid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to add folders to the view');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to add folders to the view.'), {
        cause: error,
      });
    }
  },

  async removeSourceDirectories(
    viewUid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeViewSourceDirectories({
        path: { view_uid: viewUid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove folders from the view');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to remove folders from the view.'), {
        cause: error,
      });
    }
  },

  async listSourceContracts(viewUid: string): Promise<ContractManagementContractInDbSlim[]> {
    try {
      const response = await listViewSourceContracts({ path: { view_uid: viewUid } });
      return response.data ?? [];
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to load the view’s contracts.'), {
        cause: error,
      });
    }
  },

  async addSourceContracts(
    viewUid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await addViewSourceContracts({
        path: { view_uid: viewUid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to add contracts to the view');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to add contracts to the view.'), {
        cause: error,
      });
    }
  },

  async removeSourceContracts(
    viewUid: string,
    uids: string[]
  ): Promise<ContractManagementCalendarLinkResult> {
    try {
      const response = await removeViewSourceContracts({
        path: { view_uid: viewUid },
        body: { uids },
      });
      if (!response.data) throw new Error('Failed to remove contracts from the view');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to remove contracts from the view.'), {
        cause: error,
      });
    }
  },
};
